/*
Starting here and Continuing until Noted all code is provided with RESTRICTED RIGHTS:

Restricted Rights
Contract No.
BluePath Labs
2101 L St NW, Suite 800
Washington, DC 20037
The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(3) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract. Any reproduction of computer software or portions thereof marked with this legend must also reproduce the markings. Any person, other than the Government, who has been provided access to such software must promptly notify the above named Contractor.
*/
import BenchmarksService from '@/api/benchmarks';
import filteringHelper from 'src/services/filteringHelper';
import Vue from 'vue';

// initial state
const state = {
  entities: [],
  loadState: false,
  customSetData: [],
  loadedCustomSet: null,
  institutionData: [],
  pagination: {
    from: 0,
    page: 1,
    rowsPerPage: 10,
    sort: 'Alphabetical',
    sortBy: 'desc',
    rowsNumber: null,
  },
};

const getters = {
  entities: (state) => {
    return state.entities;
  },
  loadState: (state) => {
    return state.loadState;
  },
  customSetData: (state) => {
    return state.customSetData;
  },
  loadedCustomSet: (state) => {
    return state.loadedCustomSet;
  },
  institutionData: (state) => {
    return state.institutionData;
  },
  pagination: (state) => {
    return state.pagination;
  },
};

const actions = {
  async getBenchmarks({ commit, rootGetters }, payload) {
    // If any current global filters are applied, add them to payload sent to backend
    let _filters = {};
    const source =
      payload && payload.source
        ? payload.source
        : rootGetters['app/activeDataSource'];
    const currentFiltering = rootGetters['filters/currentFiltering'][source];
    if (currentFiltering && Object.keys(currentFiltering).length > 0) {
      // Use lodash _.cloneDeep to prevent object transferrance
      _filters = _.cloneDeep(currentFiltering);
    }

    // If the applied global filters include publication year, overwrite them with yearRange in payload -- or add the property if it doesn't already exist.
    if (payload.yearRange) {
      _filters.publication_year = [payload.yearRange];
      // Delete yearRange property since it's now included in filters
      delete payload.yearRange;
    } else {
      // Delete the publication year filter if no year range is coming from the tab, ie show all time
      delete _filters.publication_year;
    }

    // Create filters property and add _filters content to it
    payload.filters = _filters;

    // Get global search value from store
    const globalSearchVal = rootGetters['filters/searchWithinResults'];
    // If there is a global search value, add it to the query
    if (globalSearchVal) {
      // If there is a query object in the payload, ie queryScope is on, use filteringHelper to add the global search value to it. Otherwise create a new query object for filteringHelper to add the global search value to.
      payload.query = filteringHelper.filterResultsText(
        payload.query ? payload.query : { query: { bool: {} } },
        globalSearchVal
      );
    }

    const response = await BenchmarksService.fetchBenchmarks(payload);
    if (response && response.success) {
      commit('setEntities', response.data);
      commit('setLoadState', true);
    }
  },
  clearData({ commit }) {
    commit('setEntities', []);
  },
  // Fetch custom set data for current scan
  async getCustomSetData({ commit, dispatch }, payload) {
    const _data = await BenchmarksService.getCustomSetData(payload);

    if (!_data.error) {
      const setData = _data.data;

      // Commit mutation to load the custom graph data array into state
      commit('setCustomSetData', setData);
      // Dispatch action to set the currently loaded custom set to the first set returned
      dispatch('setLoadedCustomSet', setData[0]);
    }
  },
  // Call backend to create a custom set, commit mutation to add new set to state
  async createCustomSet({ commit, dispatch }, payload) {
    const _data = await BenchmarksService.createCustomSet(payload);
    // Service only returns id and error object; package data as store expects with payload from component and id returned from service
    if (!_data.error) {
      const data = {
        id: _data.data[0].id,
        entities: payload.entities,
        entity_type: payload.entityType,
        is_universal: payload.isUniversal,
        name: payload.setName,
        user_id: payload.userId,
      };
      // Commit mutation to add created set to state
      commit('addNewCustomSetData', data);
      // Dispatch action to set the currently loaded custom set to the newly created set
      dispatch('setLoadedCustomSet', data);
      // Let component know request was successful
      return true;
    } else {
      // Let component know request was unsuccessful
      return false;
    }
  },
  setLoadedCustomSet({ commit }, payload) {
    commit('setLoadedCustomSet', payload);
  },
  // Call backend to edit a custom set, commit mutation to update state with new values
  async updateCustomSet({ commit, dispatch }, payload) {
    const _data = await BenchmarksService.editCustomSet(payload);
    if (!_data.error) {
      // Service only returns error object, package data as store expects with payload from component
      const data = {
        id: payload.setId,
        entities: payload.entities,
        entity_type: payload.entityType,
        is_universal: payload.isUniversal,
        name: payload.setName,
        user_id: payload.userId,
      };
      // Commit mutation to update the edited set in state
      commit('updateCustomSet', data);
      // Dispatch action to set the currently loaded custom set to the updated set
      dispatch('setLoadedCustomSet', data);
      // Let component know request was successful
      return true;
    } else {
      // Let component know request was unsuccessful
      return false;
    }
  },
  // Call backend to delete an existing custom set
  async deleteCustomSet({ commit, dispatch }, payload) {
    const _data = await BenchmarksService.deleteCustomSet(payload);
    if (!_data.error) {
      // Only id is required to delete set
      const data = { id: payload };
      // Commit mutation to remove the deleted set from state
      commit('deleteCustomSet', data);
      // Dispatch action to set the currently loaded custom set
      dispatch('setLoadedCustomSet');
      // Let component know request was successful
      return true;
    } else {
      // Let component know request was unsuccessful
      return false;
    }
  },
  // Fetch institutions data for customSetModal table
  async getInstitutionData({ commit, dispatch }, payload) {
    const _data = await BenchmarksService.getInstitutions(payload);
    if (!_data.error) {
      commit('setInstitutionData', _data);
      dispatch('setPagination', _data.pagination);
    }
  },
  setPagination({ commit }, payload) {
    commit('setPagination', payload);
  },
};

const mutations = {
  setEntities(state, payload) {
    state.entities = payload;
  },
  setLoadState(state, payload) {
    state.loadState = payload;
  },
  setCustomSetData(state, payload) {
    state.customSetData = payload;
  },
  addNewCustomSetData(state, payload) {
    state.customSetData.push(payload);
  },
  setLoadedCustomSet(state, payload) {
    //state.loadedCustomSet = payload;
    // No payload = custom set deleted
    if (!payload) {
      // If custom sets in state, set loadedCustomSet to first object in array, else set to default null state
      state.loadedCustomSet = state.customSetData.length
        ? {
            ...state.customSetData[0],
          }
        : null;
    } else {
      // Set the loaded graph to the payload
      state.loadedCustomSet = { ...payload };
    }
  },
  setInstitutionData(state, payload) {
    state.institutionData = payload;
  },
  setPagination(state, payload) {
    state.pagination = { ...payload };
  },
  // Find edited set in state by index; update set state with payload from component
  updateCustomSet(state, payload) {
    const setIndex = state.customSetData.findIndex(
      (item) => item.id === payload.id
    );

    Vue.set(state.customSetData, setIndex, payload);
  },
  deleteCustomSet(state, payload) {
    // Filter the deleted custom set by id
    state.customSetData = state.customSetData.filter(
      (data) => data.id !== payload.id
    );
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
/*
Ending here, all code as  noted above is provided with RESTRICTED RIGHTS:

Restricted Rights
Contract No.
BluePath Labs
2101 L St NW, Suite 800
Washington, DC 20037
The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(3) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract. Any reproduction of computer software or portions thereof marked with this legend must also reproduce the markings. Any person, other than the Government, who has been provided access to such software must promptly notify the above named Contractor.
*/
